import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';
import { CheckCircle2, Circle, ArrowRight } from 'lucide-react';

const CourseDetailPage = () => {
  const { id } = useParams();
  const { courses, courseProgress, updateCourseProgress } = useData();
  const navigate = useNavigate();
  const course = courses.find(c => c.id === id);

  if (!course) return <div>Course not found</div>;

  const progress = courseProgress[id];
  const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons, 0);
  const completedCount = progress?.completedLessons?.length || 0;
  const percent = Math.round((completedCount / totalLessons) * 100);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />
      <div className="wrap section">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Home</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href="/academy">Academy</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{course.title}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="mb-6">
              <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'hsl(var(--violet-soft))', color: 'hsl(var(--violet))' }}>{course.level}</span>
              <h1 className="mt-4 mb-3">{course.title}</h1>
              <p className="text-lg" style={{ color: 'hsl(var(--ink-muted))' }}>{course.summary}</p>
              <div className="flex gap-4 mt-4 text-sm" style={{ color: 'hsl(var(--ink-faint))' }}>
                <span>{course.modules.length} modules</span>
                <span>·</span>
                <span>{Math.round(course.durationMins / 60)} hours</span>
              </div>
            </div>

            {percent > 0 && (
              <Card className="p-5 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Your Progress</span>
                  <span className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>{percent}%</span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'hsl(var(--line-2))' }}>
                  <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: 'hsl(var(--mint))' }} />
                </div>
              </Card>
            )}

            <div className="space-y-4">
              {course.modules.map((module, idx) => (
                <Card key={module.id} className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))', fontWeight: '600' }}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-1">{module.title}</h4>
                      <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>{module.lessons} lessons</p>
                    </div>
                    {idx === 0 && <Button size="sm">Start</Button>}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <aside className="space-y-6">
            <Card className="p-5">
              <h4 className="font-semibold mb-4">About this course</h4>
              <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Designed for {course.level.toLowerCase()} learners in grid modernization.</p>
              <Button className="w-full mt-4">Enroll Now</Button>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
